import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Database, ExternalLink, FileSpreadsheet, Lock, ArrowRight } from "lucide-react";

import { useAuth } from "../../../context/AuthContexts";
import { usePermission } from "../../../hooks/usePermission";
import { DATASETS } from "../datasets";

/**
 * The way into the Cost Databank.
 *
 * It replaces ten sidebar entries with one: the registers are offered here, as
 * a page, rather than crowding the portal's navigation with a section most
 * members open occasionally and then read at length.
 *
 * ── The dues gate ───────────────────────────────────────────────────────────
 * The databank is a benefit of paid-up membership, so a member who is behind on
 * dues is told so plainly instead of being shown an empty or half-working
 * screen. It is deliberately a MESSAGE, not a hidden menu item: somebody who
 * cannot see the section at all learns nothing, whereas somebody told why can
 * do something about it.
 *
 * Staff without a member record are not members and are not asked for dues.
 */
/**
 * The databank's own site.
 *
 * Read from the environment so it can differ between local, staging and
 * production without a code change; the default is the live subdomain.
 */
const DATABANK_SITE =
  process.env.REACT_APP_DATABANK_SITE_URL || "https://costdata.niqsng.org";

export default function DatabankHomePage() {
  const { user, hasRole } = useAuth();
  const { can } = usePermission();
  const navigate = useNavigate();

  const isStaff = hasRole("admin") || hasRole("super_admin");
  const hasMemberRecord = !!((user as any)?.member_id || (user as any)?.member?.id);
  const upToDate = (user as any)?.is_financially_up_to_date !== false;

  const blocked = !isStaff && hasMemberRecord && !upToDate;

  const canSeeAnalysis =
    hasRole("super_admin") || can("databank.analysis.access");

  if (blocked) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 space-y-4 text-center">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>

          <div>
            <h2 className="text-lg font-black text-gray-900">
              The Cost Databank is for financially up-to-date members
            </h2>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Your dues are not settled, so the registers are closed to you for now.
              Once your outstanding subscription is paid the databank opens again —
              no further request is needed.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Go back
            </button>
            <Link
              to="/my-invoices"
              className="flex-1 px-4 py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-semibold"
            >
              View my invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-blue-700 text-white p-6 md:p-8 shadow-lg shadow-indigo-200/50">
        <div className="absolute -top-16 -right-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex items-center gap-4 min-w-0">
          <div className="p-3 rounded-2xl bg-white/15 backdrop-blur border border-white/20 shrink-0">
            <Database className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black tracking-tight">Cost Databank</h1>
            <p className="text-sm text-indigo-100/90 mt-0.5">
              The Institute's reference costs — by state, by period. Choose a register.
            </p>
          </div>
        </div>

        {/* The databank's own site. Opened in a new tab so the member keeps
            their place in the portal — they are going to read, and come back. */}
        <a
          href={DATABANK_SITE}
          target="_blank"
          rel="noopener noreferrer"
          className="relative mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-indigo-700 text-sm font-bold hover:bg-indigo-50 transition-colors shadow-sm"
        >
          <ExternalLink className="w-4 h-4" />
          Open the Databank site
        </a>
        <p className="relative text-[11px] text-indigo-100/80 mt-2">
          Sign in there with your portal account to see the full details.
        </p>
      </div>

      {/* The registers */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {DATASETS.map((d) => (
          <Link
            key={d.key}
            to={`/databank/register/${d.key}`}
            className="group bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-bold text-gray-900 group-hover:text-indigo-700 transition-colors">
                {d.label}
              </h2>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 shrink-0 mt-0.5 transition-colors" />
            </div>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{d.blurb}</p>
          </Link>
        ))}

        {/* Not a register of figures but a library of documents — kept with
            them because it is the same question asked at a larger scale. */}
        <Link
          to="/databank/elemental-costs"
          className="group bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-bold text-gray-900 group-hover:text-indigo-700 transition-colors">
              Elemental Cost Analyses
            </h2>
            <FileSpreadsheet className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 shrink-0 mt-0.5 transition-colors" />
          </div>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
            Filed analyses of completed projects, element by element.
          </p>
        </Link>
      </div>

      {/* Analysis last, as the thing you do once you know which figures you want. */}
      {canSeeAnalysis && (
        <Link
          to="/databank/analysis"
          className="group flex items-center justify-between gap-4 bg-gray-900 text-white rounded-2xl p-5 hover:bg-black transition-colors"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="p-2.5 rounded-xl bg-white/10 border border-white/15 shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold">Databank Analysis</h2>
              <p className="text-xs text-gray-300 mt-0.5">
                Statistics across the registers — spread, movement over time, and by state.
              </p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-white shrink-0 transition-colors" />
        </Link>
      )}
    </div>
  );
}
